import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { getBrowserDiagnostics, copyToClipboard, type BrowserDiagnostics } from "../utils/browserInfo";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
  showDetails: boolean;
  diagnostics: BrowserDiagnostics;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
    showDetails: false,
    diagnostics: getBrowserDiagnostics(),
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    try {
      Sentry.withScope((scope) => {
        scope.setTag("errorBoundary", true);
        scope.setExtra("componentStack", errorInfo.componentStack);
        scope.setExtra("diagnostics", this.state.diagnostics);
        Sentry.captureException(error);
      });
    } catch (e) {
      console.warn("Failed to report error to Sentry:", e);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHardReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
    } catch {
      // ignore storage errors
    }
    const cleanUrl = window.location.origin + window.location.pathname + "?_t=" + Date.now();
    window.location.replace(cleanUrl);
  };

  handleCopyReport = async () => {
    const { error, errorInfo, diagnostics } = this.state;
    const report = [
      "=== گزارش خطای آکادمی واک ===",
      `زمان: ${new Date().toLocaleString("fa-IR")}`,
      `آدرس صفحه: ${window.location.href}`,
      `مرورگر: ${diagnostics.browserName} ${diagnostics.browserVersion}`,
      `سیستم‌عامل: ${diagnostics.osName}`,
      `مرورگر داخلی برنامه: ${diagnostics.isInAppBrowser ? `بله (${diagnostics.inAppName || "نامشخص"})` : "خیر"}`,
      `وضعیت اتصال: ${diagnostics.isOnline ? "متصل به اینترنت" : "آفلاین / بدون اینترنت"}`,
      `ابعاد صفحه: ${diagnostics.screenSize}`,
      `User-Agent: ${diagnostics.userAgent}`,
      "",
      `عنوان خطا: ${error?.name || "خطای نامشخص"}`,
      `پیام خطا: ${error?.message || "بدون پیام"}`,
      "",
      "--- استک خطا ---",
      error?.stack || "بدون استک",
      "",
      "--- استک کامپوننت ---",
      errorInfo?.componentStack || "بدون استک کامپوننت",
    ].join("\n");

    const success = await copyToClipboard(report);
    if (success) {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 3000);
    }
  };

  getHumanReadableReason(): { summary: string; detail: string } {
    const msg = (this.state.error?.message || "").toLowerCase();

    if (
      msg.includes("dynamically imported module") ||
      msg.includes("loading chunk") ||
      msg.includes("failed to fetch") ||
      msg.includes("networkerror")
    ) {
      return {
        summary: "خطا در دریافت فایل‌های به‌روزرسانی شده برنامه از سرور",
        detail:
          "این خطا معمولاً به دلیل قطع و وصلی اینترنت، فیلترینگ یا انتشار نسخه جدید وب‌سایت رخ می‌دهد. با بارگذاری مجدد برطرف خواهد شد.",
      };
    }

    if (msg.includes("quotaexceeded") || msg.includes("storage") || msg.includes("securityerror")) {
      return {
        summary: "محدودیت دسترسی به حافظه محلی مرورگر (Storage)",
        detail:
          "مرورگر شما در حالت خصوصی (Private/Incognito) یا با تنظیمات امنیتی است که مانع ذخیره اطلاعات ضروری می‌شود.",
      };
    }

    if (!this.state.diagnostics.isOnline) {
      return {
        summary: "عدم اتصال به شبکه اینترنت",
        detail: "دستگاه شما به اینترنت متصل نیست یا شبکه قطع شده است.",
      };
    }

    return {
      summary: "بروز خطای غیرمنتظره در پردازش کدهای رابط کاربری",
      detail:
        "بخشی از برنامه در زمان پردازش یا نمایش اطلاعات با مشکلی مواجه شد که امکان ادامه کار را مختل کرد.",
    };
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, copied, showDetails, diagnostics } = this.state;
      const { summary, detail } = this.getHumanReadableReason();

      return (
        <div
          dir="rtl"
          className="flex min-h-screen items-center justify-center bg-slate-50 p-4 font-[Kalameh,Tahoma,sans-serif]"
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-900/5 sm:p-8">
            {/* Header / Icon */}
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <span className="inline-block rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                  خطای سیستمی در فرانت‌اند
                </span>
                <h1 className="mt-1 text-xl font-bold text-slate-800 sm:text-2xl">
                  متأسفانه در اجرای وب‌سایت خطایی رخ داد
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {summary}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {detail}
                </p>
              </div>
            </div>

            {/* In-app browser warning banner */}
            {diagnostics.isInAppBrowser && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs leading-relaxed text-amber-900">
                <div className="flex items-center gap-2 font-bold">
                  <svg className="h-4 w-4 shrink-0 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>هشدار مرورگر داخلی {diagnostics.inAppName}</span>
                </div>
                <p className="mt-1 text-amber-800">
                  شما در حال مشاهده سایت از داخل پیام‌رسان هستید. این مرورگرها معمولاً در اجرای کامل اسکریپت‌ها مشکل دارند. لطفاً از منوی سه نقطه (⋮) گزینه <strong>«باز کردن در مرورگر اصلی» (Chrome یا Safari)</strong> را انتخاب کنید.
                </p>
              </div>
            )}

            {/* Diagnostic info table */}
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 text-xs">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <span className="text-slate-400">مرورگر: </span>
                  <span className="font-semibold text-slate-700">
                    {diagnostics.browserName} {diagnostics.browserVersion || ""}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">سیستم‌عامل: </span>
                  <span className="font-semibold text-slate-700">{diagnostics.osName}</span>
                </div>
                <div>
                  <span className="text-slate-400">وضعیت اتصال: </span>
                  <span className={`font-semibold ${diagnostics.isOnline ? "text-emerald-600" : "text-rose-600"}`}>
                    {diagnostics.isOnline ? "متصل به شبکه" : "قطع اتصال اینترنت"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">کد پیام: </span>
                  <span className="font-mono text-slate-700 truncate inline-block max-w-[200px] align-bottom">
                    {error?.name || "Error"}: {error?.message || "نامشخص"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-98"
              >
                تلاش مجدد و بارگذاری صفحه
              </button>

              <button
                type="button"
                onClick={this.handleHardReset}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-98"
              >
                پاکسازی حافظه موقت و رفرش
              </button>

              <button
                type="button"
                onClick={this.handleCopyReport}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 active:scale-98"
              >
                {copied ? "✓ گزارش خطا کپی شد" : "کپی اطلاعات خطا"}
              </button>
            </div>

            {/* Technical details toggle */}
            <div className="mt-6 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => this.setState({ showDetails: !showDetails })}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                <span>{showDetails ? "بستن جزئیات فنی خطا" : "مشاهده جزئیات فنی خطا (مخصوص پشتیبانی)"}</span>
                <svg
                  className={`h-4 w-4 transition-transform ${showDetails ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              {showDetails && (
                <div className="mt-3 max-h-60 overflow-auto rounded-lg bg-slate-900 p-3 text-left font-mono text-[11px] text-slate-200 dir-ltr">
                  <div className="text-red-400 font-bold mb-1">
                    {error?.name}: {error?.message}
                  </div>
                  {error?.stack && (
                    <div className="whitespace-pre-wrap text-slate-300 opacity-90">
                      {error.stack}
                    </div>
                  )}
                  {errorInfo?.componentStack && (
                    <div className="mt-2 border-t border-slate-700 pt-2 text-slate-400 whitespace-pre-wrap">
                      Component Stack:
                      {errorInfo.componentStack}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
